/**
 * Página de Gestão de Leads
 * Lista completa de todos os leads com filtros
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Stack,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import {
  PeopleOutlined,
  PhoneOutlined,
  WhatsApp,
  LanguageOutlined,
  RefreshOutlined,
  ClearAllOutlined,
  VisibilityOutlined
} from '@mui/icons-material';
import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';

interface LeadData {
  id: string;
  name: string;
  address: string;
  city?: string;
  neighborhood?: string;
  state?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  website?: string;
  status: string;
  groupName: string;
  userName?: string;
  userEmail?: string;
  createdAt: string;
}

interface FilterOptions {
  cities: string[];
  neighborhoods: string[];
  states: string[];
  users: Array<{ uid: string; displayName: string; email: string }>;
}

const LeadsPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [leads, setLeads] = useState<LeadData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    cities: [],
    neighborhoods: [],
    states: [],
    users: []
  });

  // Filtros
  const [filterState, setFilterState] = useState<string>('');
  const [filterCity, setFilterCity] = useState<string>('');
  const [filterNeighborhood, setFilterNeighborhood] = useState<string>('');
  const [filterUser, setFilterUser] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Carregar leads e filtros
  useEffect(() => {
    loadLeads();
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      // Se não for admin, filtrar por usuário
      if (!isAdmin && user) {
        queryParams.append('userId', user.uid);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/leads?${queryParams.toString()}`);
      const data = await response.json();
      setLeads(data);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilters = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/leads/filters`);
      const data = await response.json();
      setFilterOptions(data);
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
    }
  };

  const handleClearFilters = () => {
    setFilterState('');
    setFilterCity('');
    setFilterNeighborhood('');
    setFilterUser('');
    setFilterStatus('');
    setSearchTerm('');
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'novo': 'Novo',
      'contactado': 'Contactado',
      'recusou': 'Recusou',
      'semContato': 'Sem Contato',
      'sem_contato': 'Sem Contato',
      'emContato': 'Em Contato',
      'em_contato': 'Em Contato',
      'fechado': 'Fechado'
    };
    return labels[status] || 'Novo';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'novo': '#757575',
      'contactado': '#2196f3',
      'recusou': '#f44336',
      'semContato': '#ff9800',
      'sem_contato': '#ff9800',
      'emContato': '#9c27b0',
      'em_contato': '#9c27b0',
      'fechado': '#4caf50'
    };
    return colors[status] || '#757575';
  };

  // Aplicar filtros
  const filteredLeads = leads.filter(lead => {
    // Filtro de busca por nome
    if (searchTerm && !lead.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Filtro por estado
    if (filterState && lead.state !== filterState) {
      return false;
    }

    // Filtro por cidade
    if (filterCity && lead.city !== filterCity) {
      return false;
    }

    // Filtro por bairro
    if (filterNeighborhood && lead.neighborhood !== filterNeighborhood) {
      return false;
    }

    // Filtro por usuário (agente)
    if (filterUser && lead.userEmail !== filterUser) {
      return false;
    }

    // Filtro por status
    if (filterStatus && lead.status !== filterStatus) {
      return false;
    }

    return true;
  });

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
              Gestão de Leads
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {filteredLeads.length} leads {filterState || filterCity || filterNeighborhood || filterUser || filterStatus ? 'filtrados' : 'no total'}
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={loadLeads}
            sx={{
              color: '#FF6B35',
              borderColor: '#FF6B35',
              '&:hover': {
                borderColor: '#FF8A5B',
                backgroundColor: 'rgba(255, 107, 53, 0.1)',
              }
            }}
          >
            Atualizar
          </Button>
        </Box>

        {/* Filtros */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: '#1a1a1a',
            border: '1px solid rgba(255, 107, 53, 0.1)',
          }}
        >
          <Stack spacing={2}>
            {/* Linha 1: Busca e Status */}
            <Stack direction="row" spacing={2}>
              <TextField
                placeholder="Buscar por nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#FF6B35' },
                  }
                }}
              />

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e: SelectChangeEvent) => setFilterStatus(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6B35' },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="novo">Novo</MenuItem>
                  <MenuItem value="contactado">Contactado</MenuItem>
                  <MenuItem value="recusou">Recusou</MenuItem>
                  <MenuItem value="semContato">Sem Contato</MenuItem>
                  <MenuItem value="emContato">Em Contato</MenuItem>
                  <MenuItem value="fechado">Fechado</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Linha 2: Localização */}
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Estado</InputLabel>
                <Select
                  value={filterState}
                  label="Estado"
                  onChange={(e: SelectChangeEvent) => setFilterState(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6B35' },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {filterOptions.states.map(state => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Cidade</InputLabel>
                <Select
                  value={filterCity}
                  label="Cidade"
                  onChange={(e: SelectChangeEvent) => setFilterCity(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6B35' },
                  }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {filterOptions.cities.map(city => (
                    <MenuItem key={city} value={city}>{city}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Bairro</InputLabel>
                <Select
                  value={filterNeighborhood}
                  label="Bairro"
                  onChange={(e: SelectChangeEvent) => setFilterNeighborhood(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6B35' },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {filterOptions.neighborhoods.map(neighborhood => (
                    <MenuItem key={neighborhood} value={neighborhood}>{neighborhood}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isAdmin && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Agente</InputLabel>
                  <Select
                    value={filterUser}
                    label="Agente"
                    onChange={(e: SelectChangeEvent) => setFilterUser(e.target.value)}
                    sx={{
                      color: '#FFFFFF',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6B35' },
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {filterOptions.users.map(usr => (
                      <MenuItem key={usr.uid} value={usr.email}>
                        {usr.displayName || usr.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Button
                variant="outlined"
                startIcon={<ClearAllOutlined />}
                onClick={handleClearFilters}
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }
                }}
              >
                Limpar
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Tabela de Leads */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF6B35' }} />
          </Box>
        ) : filteredLeads.length === 0 ? (
          <Paper
            sx={{
              p: 8,
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.1)',
              textAlign: 'center',
            }}
          >
            <PeopleOutlined sx={{ fontSize: 80, color: 'rgba(255, 107, 53, 0.3)', mb: 2 }} />
            <Typography variant="h5" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
              Nenhum lead encontrado
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              {searchTerm || filterState || filterCity || filterNeighborhood || filterUser || filterStatus
                ? 'Tente ajustar os filtros'
                : 'Importe leads da busca para começar'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.1)',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold', minWidth: 180 }}>Nome</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold', minWidth: 150 }}>Localização</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold', minWidth: 130 }}>Contato</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold', minWidth: 120 }}>Status</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold', minWidth: 150 }}>Grupo</TableCell>
                  {isAdmin && (
                    <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold', minWidth: 140 }}>Agente</TableCell>
                  )}
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold', width: 80 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLeads.map((lead, index) => (
                  <TableRow 
                    key={lead.id}
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: 'rgba(255, 107, 53, 0.05)' },
                      '& td': { color: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">
                        {index + 1}
                      </Typography>
                    </TableCell>

                    {/* Nome */}
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {lead.name}
                      </Typography>
                    </TableCell>

                    {/* Localização */}
                    <TableCell>
                      <Stack spacing={0.3}>
                        {lead.city && (
                          <Typography variant="body2">
                            {lead.city}{lead.state ? ` - ${lead.state}` : ''}
                          </Typography>
                        )}
                        {lead.neighborhood && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            {lead.neighborhood}
                          </Typography>
                        )}
                        {!lead.city && !lead.neighborhood && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                            Não informado
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Contato */}
                    <TableCell>
                      <Stack spacing={0.5}>
                        {lead.phone && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PhoneOutlined sx={{ fontSize: '0.9rem', color: '#FF6B35' }} />
                            <Typography variant="body2">{lead.phone}</Typography>
                            {lead.whatsapp && (
                              <Tooltip title="Abrir WhatsApp">
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.3 }}
                                  onClick={() => window.open(`https://wa.me/${lead.whatsapp?.replace(/\D/g, '')}`, '_blank')}
                                >
                                  <WhatsApp sx={{ fontSize: '1rem', color: '#25D366' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                        {lead.website && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <LanguageOutlined sx={{ fontSize: '0.9rem', color: '#FF6B35' }} />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                maxWidth: 150, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {lead.website}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={getStatusLabel(lead.status)}
                        size="small"
                        sx={{
                          backgroundColor: `${getStatusColor(lead.status)}20`,
                          color: getStatusColor(lead.status),
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: `1px solid ${getStatusColor(lead.status)}40`,
                        }}
                      />
                    </TableCell>

                    {/* Grupo */}
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        {lead.groupName}
                      </Typography>
                    </TableCell>

                    {/* Agente (só admin vê) */}
                    {isAdmin && (
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {lead.userName || lead.userEmail || '-'}
                        </Typography>
                      </TableCell>
                    )}

                    {/* Ações */}
                    <TableCell>
                      <Tooltip title="Ver detalhes">
                        <IconButton
                          size="small"
                          sx={{
                            color: '#FF6B35',
                            '&:hover': { backgroundColor: 'rgba(255, 107, 53, 0.1)' }
                          }}
                        >
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </MainLayout>
  );
};

export default LeadsPage;
